"use client";

import { useState } from "react";
import Link from "next/link";
import { toast } from "sonner";
import {
  Check,
  Copy,
  Clock,
  Palette,
  CalendarHeart,
  Heart,
  ShoppingBag,
  HelpCircle,
  ArrowRight,
  Phone,
  Mail,
  PenLine,
} from "lucide-react";
import {
  SiWhatsapp,
  SiInstagram,
  SiWechat,
  SiXiaohongshu,
} from "@icons-pack/react-simple-icons";
import { config } from "@/lib/config";
// Opening hours come from the canonical NAP source (lib/brand.ts) — never
// hardcoded, so a change there propagates to every surface at once.
import { brand } from "@/lib/brand";
import { SOCIAL_COLORS } from "@/lib/social-colors";
import {
  trackWhatsappClick,
  trackInstagramClick,
  trackPhoneClick,
} from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHero } from "@/components/landing/PageHero";
import { EnquiryForm } from "./EnquiryForm";

export function EnquireClient() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setCopiedId(id);
    toast.success("WeChat ID copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <>
      {/* ─── Hero ─── */}
      <PageHero
        src="/asset/enquire-delivery.webp"
        alt="Bespoke bouquet wrapped and ready for delivery in Leeds"
        variant="with-cta"
        ariaLabel="Contact The Floral Muse"
        subtitle="Get in Touch"
        title="Bespoke & Custom Flowers in Leeds"
        description="For a bespoke design or a quick question, message us and we'll create something meaningful. Ready-made pieces can be ordered straight from the shop."
      >
        <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Button
            asChild
            size="lg"
            className="w-full sm:w-auto sm:min-w-48 bg-[#25D366] text-white hover:bg-[#20bd5a]"
          >
            <a
              href={config.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsappClick("enquire_hero")}
            >
              <SiWhatsapp size={18} />
              Message on WhatsApp
              <span className="sr-only"> (opens in new tab)</span>
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full sm:w-auto sm:min-w-48 border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white"
          >
            <a
              href={`tel:${config.phone}`}
              onClick={() => trackPhoneClick("enquire_hero")}
            >
              <Phone className="h-4 w-4" />
              Call {config.phoneDisplay}
            </a>
          </Button>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-white/70">
          <Clock className="h-3.5 w-3.5" />
          <span>We reply personally, {brand.hours.display}</span>
        </div>
      </PageHero>

      {/* ─── Contact channels (primary path) ─── */}
      <section className="bg-muted px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="heading-section text-center">Message Us Directly</h2>
          <p className="mx-auto mt-2 max-w-2xl text-center text-muted-foreground">
            Planning something bespoke, or have a question? A quick message is the
            fastest way — we reply personally on every channel below. For ready-made
            pieces, just shop online and check out securely.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* WhatsApp */}
            <a
              href={config.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsappClick("enquire_channels")}
              className="group flex flex-col items-center gap-3 rounded-xl border border-border/40 bg-background p-6 text-center transition-colors hover:border-[#25D366]/30 hover:bg-[#25D366]/5"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366]/10">
                <SiWhatsapp size={24} color="#25D366" />
              </div>
              <div>
                <p className="font-serif text-base font-semibold text-foreground">WhatsApp</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Fastest way to reach us
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-[#25D366]">
                Start a chat <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="sr-only"> (opens in new tab)</span>
            </a>

            {/* Instagram */}
            <a
              href={config.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackInstagramClick("enquire_channels")}
              className="group flex flex-col items-center gap-3 rounded-xl border border-border/40 bg-background p-6 text-center transition-colors hover:border-primary/30 hover:bg-muted/50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <SiInstagram size={24} className="text-muted-foreground" />
              </div>
              <div>
                <p className="font-serif text-base font-semibold text-foreground">Instagram</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  DM us or browse our work
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-primary">
                Open Instagram <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="sr-only"> (opens in new tab)</span>
            </a>

            {/* WeChat */}
            <button
              type="button"
              onClick={() => handleCopy(config.wechatId, "wechat")}
              className="group flex flex-col items-center gap-3 rounded-xl border border-border/40 bg-background p-6 text-center transition-colors hover:border-[#07C160]/30 hover:bg-[#07C160]/5"
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: SOCIAL_COLORS.wechat.bg }}
              >
                <SiWechat size={24} color={SOCIAL_COLORS.wechat.icon} />
              </div>
              <div>
                <p className="font-serif text-base font-semibold text-foreground">WeChat</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  ID: {config.wechatId}
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-[#07C160]">
                {copiedId === "wechat" ? (
                  <><Check className="h-3 w-3" /> Copied!</>
                ) : (
                  <><Copy className="h-3 w-3" /> Copy ID</>
                )}
              </span>
            </button>

            {/* Xiaohongshu */}
            <a
              href={config.xiaohongshu}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 rounded-xl border border-border/40 bg-background p-6 text-center transition-colors hover:border-[#FF2442]/20 hover:bg-[#FF2442]/5"
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: SOCIAL_COLORS.xiaohongshu.bg }}
              >
                <SiXiaohongshu size={24} color={SOCIAL_COLORS.xiaohongshu.icon} />
              </div>
              <div>
                <p className="font-serif text-base font-semibold text-foreground">Xiaohongshu</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Find us for inspiration
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-[#FF2442]">
                Visit profile <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="sr-only"> (opens in new tab)</span>
            </a>

            {/* Email */}
            <a
              href={`mailto:${config.contactEmail}`}
              className="group flex flex-col items-center gap-3 rounded-xl border border-border/40 bg-background p-6 text-center transition-colors hover:border-primary/30 hover:bg-muted/50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-serif text-base font-semibold text-foreground">Email</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Reply within 24 hours
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-primary">
                Send email <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>


            {/* Phone */}
            <a
              href={`tel:${config.phone}`}
              onClick={() => trackPhoneClick("enquire_channels")}
              className="group flex flex-col items-center gap-3 rounded-xl border border-border/40 bg-background p-6 text-center transition-colors hover:border-primary/30 hover:bg-muted/50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Phone className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <p className="font-serif text-base font-semibold text-foreground">Phone</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {config.phoneDisplay}
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-primary">
                Call now <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ─── Written-brief form (secondary path, button-triggered) ─── */}
      <section className="bg-background px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl text-center">
          <div className="mx-auto max-w-2xl">
            <h2 className="heading-section">Prefer a Written Brief?</h2>
            <p className="mt-2 text-muted-foreground">
              Rather write it down? Open a short form and share your occasion,
              colours, date and budget — we&apos;ll reply within hours. Not sure yet?
              The occasion alone is enough to start.
            </p>
          </div>
          <Button
            size="lg"
            className="mt-8 w-full sm:w-auto sm:min-w-48"
            aria-haspopup="dialog"
            onClick={() => setFormOpen(true)}
          >
            <PenLine className="h-4 w-4" />
            Open enquiry form
          </Button>
        </div>
      </section>

      {/* Written-brief form in a modal — keeps it off the page flow so opening
          it doesn't push the page taller and force the user to scroll. Radix
          traps focus + moves it to the first field; the dialog body scrolls
          internally (max-h) rather than growing the page. */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90dvh] gap-0 overflow-y-auto p-6 sm:max-w-2xl">
          <DialogHeader className="text-left">
            <DialogTitle className="heading-card">Send a written brief</DialogTitle>
            <DialogDescription>
              Share your occasion, colours, date and budget — we&apos;ll reply
              within hours. Not sure yet? The occasion alone is enough to start.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5">
            <EnquiryForm bare />
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── How it works ─── */}
      <section className="bg-muted px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="heading-section text-center">How Bespoke Orders Work</h2>
          <p className="mt-2 text-center text-muted-foreground">
            For a custom piece, here&apos;s what to expect from first message to doorstep. Prefer something ready-made? Browse the shop and check out in a couple of taps.
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                num: "01",
                title: "You message us",
                desc: "Share the occasion, preferred colours and style, date needed, and product type. Photos or mood boards are welcome.",
              },
              {
                num: "02",
                title: "We design options",
                desc: "We suggest arrangements that match your vision, confirm pricing and availability, and agree on all details.",
              },
              {
                num: "03",
                title: "We create & deliver",
                desc: "Your bespoke piece is handcrafted and delivered beautifully — fresh flowers across Leeds and West Yorkshire, preserved & crochet UK-wide.",
              },
            ].map((step) => (
              <div
                key={step.num}
                className="flex flex-col items-center rounded-xl bg-background p-6 text-center"
              >
                <span className="font-[family-name:var(--font-caveat)] text-3xl text-sage-text">
                  {step.num}
                </span>
                <h3 className="mt-3 font-serif text-base font-semibold text-foreground">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── What to include ─── */}
      <section className="bg-background px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="heading-section text-center">What to Tell Us When You Enquire</h2>
          <p className="mt-2 text-center text-muted-foreground">
            The more you share, the better we can design something you&apos;ll love. Not sure yet? Just tell us the occasion — we&apos;ll guide you through the rest.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Heart,
                title: "The occasion",
                desc: "Birthday, anniversary, sympathy, wedding, or just because.",
              },
              {
                icon: Palette,
                title: "Colours & style",
                desc: "Soft pastels, bold & bright, romantic reds — or let us surprise you.",
              },
              {
                icon: CalendarHeart,
                title: "Date needed",
                desc: "When do you need it? Same-day flower delivery may be possible for Leeds orders.",
              },
              {
                icon: ShoppingBag,
                title: "Product type",
                desc: "Fresh bouquet, preserved flowers, or hand-crocheted flowers.",
              },
            ].map((tip) => (
              <Card
                key={tip.title}
                className="border-transparent bg-muted text-center transition-colors hover:border-primary/30"
              >
                <CardContent className="flex flex-col items-center px-5 pt-8 pb-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <tip.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mt-4 font-serif text-base font-semibold text-foreground">
                    {tip.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {tip.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ + Browse CTA ─── */}
      <section className="bg-muted px-6 py-12 md:px-10 md:py-16 lg:px-16">
        <div className="mx-auto max-w-7xl text-center">
          <h2 className="heading-section">Questions Before You Order?</h2>
          <p className="mt-3 text-muted-foreground">
            Check our FAQ for answers about ordering, delivery areas, pricing, and care instructions.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              asChild
              variant="default"
              size="lg"
              className="w-full sm:w-auto sm:min-w-48"
            >
              <Link href="/faq">
                <HelpCircle className="h-4 w-4" />
                View FAQ
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="w-full sm:w-auto sm:min-w-48"
            >
              <Link href="/products">
                <ShoppingBag className="h-4 w-4" />
                Browse products
              </Link>
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link href="/gallery" className="group inline-flex items-center gap-1.5 font-medium text-sage-text transition-colors hover:text-foreground">
              View our gallery
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link href="/flower-delivery-leeds" className="group inline-flex items-center gap-1.5 font-medium text-sage-text transition-colors hover:text-foreground">
              Leeds delivery areas
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link href="/flower-care" className="group inline-flex items-center gap-1.5 font-medium text-sage-text transition-colors hover:text-foreground">
              Flower care guide
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link href="/about" className="group inline-flex items-center gap-1.5 font-medium text-sage-text transition-colors hover:text-foreground">
              About our studio
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
